import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ConfirmationModal } from 'components/organisms/modals/confirmation-modal'
import { STRIPE_CONNECTED_CALLBACK_KEY } from 'utils/constants'

export interface StripeSetupWarningModalProps {
	open: boolean
	setOpen: (open: boolean) => void
	/** If true, user has connected an account but may need to complete setup. Used for button label context. Always navigates to the Stripe account page. */
	hasConnectAccount: boolean
	/** Controls the context-specific wording. Defaults to 'ticket'. */
	type?: 'ticket' | 'offer'
}

export function StripeSetupWarningModal({
	open,
	setOpen,
	hasConnectAccount,
	type = 'ticket',
}: StripeSetupWarningModalProps) {
	const navigate = useNavigate()
	const location = useLocation()

	const handleConfirm = () => {
		setOpen(false)
		// Pass the current location so the Stripe page can return here after a successful setup.
		// forceRestoreDraft=true tells the create page to restore silently (no popup) on return.
		const params = new URLSearchParams(location.search)
		params.set('forceRestoreDraft', 'true')
		const returnUrl = `${location.pathname}?${params.toString()}`
		// Belt-and-suspenders: also stash it so it survives the Stripe OAuth redirect round-trip
		// even if the query param is dropped before the Stripe page mounts.
		try {
			sessionStorage.setItem(STRIPE_CONNECTED_CALLBACK_KEY, returnUrl)
		} catch {
			// ignore storage failures
		}
		navigate(`/manage/rep-clubs/events/revenue/stripe?connected_callback_url=${encodeURIComponent(returnUrl)}`)
	}

	return (
		<ConfirmationModal
			title="Stripe payout setup required"
			open={open}
			setOpen={setOpen}
			onConfirm={handleConfirm}
			onCancel={() => setOpen(false)}
			confirmLabel={hasConnectAccount ? 'Continue Stripe setup' : 'Go to profile'}
			cancelLabel="Cancel"
		>
			<div className="mb-4">
				To add paid {type === 'offer' ? 'offers' : 'tickets'}, you need to complete your Stripe payout setup.
				Please finish connecting and activating your Stripe account before creating paid{' '}
				{type === 'offer' ? 'offers' : 'tickets'}.
			</div>
		</ConfirmationModal>
	)
}

// ---------------------------------------------------------------------------
// Modal Manager integration
// ---------------------------------------------------------------------------

export interface StripeSetupWarningModalManagedProps {
	hasConnectAccount: boolean
	type?: 'ticket' | 'offer'
}

declare module 'components/organisms/modals/modal-registry' {
	interface ModalRegistry {
		'stripe-setup-warning': StripeSetupWarningModalManagedProps
	}
}

export function ManagedStripeSetupWarningModal({
	onClose,
	...rest
}: StripeSetupWarningModalManagedProps & { onClose: () => void }) {
	const [open, setOpen] = useState(true)

	const handleClose = () => {
		setOpen(false)
		setTimeout(onClose, 200)
	}

	return (
		<StripeSetupWarningModal
			open={open}
			setOpen={(v: boolean) => { if (!v) handleClose() }}
			{...rest}
		/>
	)
}
